/**
 * LoomSpine — vertical thread of section nodes; the mid-run surface.
 *
 * Each section in the doc is a dot on the spine. Status drives colour,
 * size, halo. The active node pulses; nearby nodes stay full-opacity;
 * distant nodes recede to ~35% so the eye lands on "now". Click a
 * node → fires onSectionFocus (Phase 4 wires this to the Inspector).
 *
 * Visual treatments (per the design):
 *   queued          — 10px hollow dot, ink5 stroke
 *   drafting/active — 14px hot-orange filled, ring halo, pulse
 *   drafted         — 12px cool-green filled
 *   needs_approval  — 14px warn-amber filled, pulse, popout below
 *   flagged         — 12px warn-amber filled with "!", or danger-red
 *
 * Animations honour ``prefers-reduced-motion`` — pulse + ambient
 * motes degrade to a static halo + nothing.
 *
 * SRP: pure presentation. Receives sections + activeSectionId via
 * props. Emits onSectionFocus on click. Doesn't subscribe to SSE,
 * doesn't fetch, doesn't dispatch.
 *
 * Phase 2 boundary: this component renders the spine. The Inspector
 * (Phase 3-4) opens INLINE under a clicked node and uses the same
 * onSectionFocus contract. The approval popout (Phase 5) renders
 * under a node when its status is needs_approval.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import React, { useCallback, useEffect, useRef, useState } from "react";
import type { SpineCounters } from "../eventLog";
import type { SpineSection } from "../hooks/useVariantCRun";
import { type AgentActivityMap, hasAnyActivity } from "../services/agentActivityFeed";
import { type LiveDocLoadMap, hasActiveLiveDocLoad } from "../services/liveDocLoadTracker";
import type { InitProgress, InitStage } from "../sseAdapter";
import { formatGutterNumber } from "../sectionLabel";
import { formatTokens } from "../utils/formatTokens";
import { spineDepth } from "../utils/spineOrder";

/** Title indent per hierarchy level (px). Small: the taskpane is narrow. */
const SPINE_INDENT_PX = 8;
import type { PendingApproval } from "../state";
import {
	brandColors,
	fonts,
	inkColors,
	motion,
	statusColors,
	surfaceColors,
} from "../tokens";
import ApprovalPopout from "./ApprovalPopout";
import SpineTagStrip from "./SpineTagStrip";

interface LoomSpineProps {
	sections: SpineSection[];
	activeSectionId: string | null;
	/** Fired when the writer clicks any node — host opens the inspector. */
	onSectionFocus?: (sectionId: string) => void;
	/** Fired when the writer clicks a section's ``N⚖`` regulator badge — host
	 *  focuses the section AND opens the inspector's Review tab so the persisted
	 *  regulatory findings (mini-reports) are shown without re-running the audit. */
	onOpenReview?: (sectionId: string) => void;
	/** Streamed connection error, if any (renders as a top banner). */
	streamError?: string | null;
	/** Pending approval, if any. Rendered as an inline popout under the
	 *  matching section's spine row. Null when no approval is pending. */
	pendingApproval?: PendingApproval | null;
	/** Writer accepted the approval. Host dispatches APPROVAL_RESOLVED
	 *  and flips the section status back to drafted. */
	onApprovalAccept?: () => void;
	/** Writer wants to inspect the findings. Host opens the inspector
	 *  for that section + Review tab. */
	onApprovalOpenReview?: () => void;
	/** Per-section cumulative event counters sourced from
	 *  ``state.eventLog.counters``. Absent section ⇒ no pill rendered
	 *  (SpineTagStrip also self-hides on total=0). Owned by the shell;
	 *  LoomSpine is a pure consumer per Spec 17's single-writer rule. */
	countersBySection?: ReadonlyMap<string, SpineCounters>;
	/** Initialization progress emitted while the backend runner is being
	 *  built (loading libraries, indexing documents). Rendered as a stage
	 *  checklist panel with a determinate progress bar when the backend
	 *  sends counts. Null / undefined once the run starts. */
	initStatus?: InitProgress | null;
	/** When true, preserve status markers but suppress motion effects
	 *  (pulse, motes, elapsed ticking) so paused runs appear static. */
	pauseAnimations?: boolean;
	/** Per-section "loading into Word…" tracker, keyed by section id. Present
	 *  entry ⇒ the section's redraft is being written into the live doc
	 *  (``loading``) or the write found no matching heading (``failed``). Owned
	 *  by the shell (VariantCShell) and cleared on a confirmed write. */
	liveDocLoadBySection?: LiveDocLoadMap;
	/** Per-section LIVE agent activity feed — the last few tool calls / retrieval
	 *  steps the agent made, indented by sub-agent depth. Shell-owned (survives
	 *  the section_state_changed subline wipe). Rendered under the active
	 *  drafting row in place of the single-line subline. */
	agentActivityBySection?: AgentActivityMap;
}

// ---------------------------------------------------------------------------
// Init checklist derivation
// ---------------------------------------------------------------------------

export interface InitChecklistStep {
	id: "connect" | "verify" | "index" | "finalize";
	label: string;
	state: "done" | "active" | "pending";
	/** Determinate counts — only set on the index step when known. */
	progress?: { done: number; total: number };
}

const INIT_STEP_DEFS: ReadonlyArray<{
	id: InitChecklistStep["id"];
	label: string;
}> = [
	{ id: "connect", label: "Connecting to libraries" },
	{ id: "verify", label: "Checking source retrieval" },
	{ id: "index", label: "Indexing documents" },
	{ id: "finalize", label: "Preparing drafting agent" },
];

const STAGE_TO_STEP: Record<InitStage, number> = {
	// Supervisor-side subprocess boot, before the runner exists — render as the
	// message-only banner (checklist returns null), not a checklist step: nothing
	// is connecting to libraries yet. The real 4-step checklist takes over once
	// the child emits its first ``libraries`` init_progress.
	booting: -1,
	preparing: 0,
	libraries: 0,
	table_spec: 1,
	health_check: 1,
	indexing: 2,
	finalizing: 3,
	// Activation resolution happens after the checklist completes — render
	// as the message-only banner (checklist returns null), not a step.
	activation: -1,
	unknown: -1,
};

/**
 * Map the latest init event to the 4-step checklist. Pure — every
 * backend init event is self-describing (full stage + counts), so no
 * accumulation across events is needed; the panel is always correct
 * from any single event, including after an SSE reconnect mid-init.
 *
 * Returns null for the "unknown" stage (older server without
 * init_stage) — the caller falls back to a message-only banner.
 */
export function deriveInitChecklist(
	init: InitProgress,
): InitChecklistStep[] | null {
	const activeIdx = STAGE_TO_STEP[init.stage];
	if (activeIdx < 0) return null;
	return INIT_STEP_DEFS.map((def, i) => {
		const state: InitChecklistStep["state"] =
			i < activeIdx ? "done" : i === activeIdx ? "active" : "pending";
		const step: InitChecklistStep = { id: def.id, label: def.label, state };
		if (
			i === activeIdx &&
			init.stage === "indexing" &&
			typeof init.done === "number" &&
			typeof init.total === "number" &&
			init.total > 0
		) {
			step.progress = { done: init.done, total: init.total };
		}
		return step;
	});
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
	root: {
		flex: 1,
		minHeight: 0,
		position: "relative",
		background: surfaceColors.paper,
		overflow: "hidden",
	},
	viewport: {
		height: "100%",
		overflow: "auto",
		position: "relative",
		padding: "16px 0 16px",
	},
	
	errorBanner: {
		position: "sticky",
		top: 0,
		margin: "0 12px 8px",
		padding: "6px 10px",
		background: "#FEF2F2",
		border: "1px solid #FCA5A5",
		borderRadius: "3px",
		fontSize: "11px",
		color: "#7F1D1D",
		zIndex: 4,
	},
	// Init panel — stage checklist shown while the backend builds the
	// runner (library profiles → retrieval check → document indexing →
	// prompt assembly). Same sticky slot + surface recipe as the old
	// single-line banner; removed in one place when run_metadata clears
	// the initStatus prop.
	initPanel: {
		position: "sticky",
		top: 0,
		margin: "0 12px 8px",
		padding: "8px 10px",
		background: surfaceColors.paper,
		border: `1px solid ${surfaceColors.lineStrong}`,
		borderRadius: "3px",
		fontSize: "11px",
		color: inkColors.ink3,
		zIndex: 4,
	},
	initPanelTitle: {
		display: "flex",
		alignItems: "center",
		gap: "6px",
		fontFamily: fonts.mono,
		fontSize: "10px",
		letterSpacing: "0.06em",
		textTransform: "uppercase",
		color: inkColors.ink3,
		marginBottom: "4px",
	},
	initBannerIcon: {
		opacity: 0.5,
	},
	initStep: {
		display: "flex",
		alignItems: "center",
		gap: "6px",
		padding: "2px 0",
	},
	initStepLabel: {
		color: inkColors.ink3,
	},
	initStepPending: {
		color: inkColors.ink4,
	},
	initStepCount: {
		marginLeft: "auto",
		fontFamily: fonts.mono,
		fontSize: "10px",
		color: inkColors.ink4,
	},
	initStepMarker: {
		flexShrink: 0,
		width: "6px",
		height: "6px",
		borderRadius: "3px",
		border: `1px solid ${inkColors.ink5}`,
		background: "transparent",
	},
	initStepMarkerDone: {
		border: "none",
		background: statusColors.cool,
	},
	initStepMarkerActive: {
		border: "none",
		background: brandColors.mid,
		boxShadow: `0 0 6px ${brandColors.glow}`,
		animationName: {
			"0%": { opacity: 1 },
			"50%": { opacity: 0.45 },
			"100%": { opacity: 1 },
		},
		animationDuration: "1600ms",
		animationTimingFunction: "ease-in-out",
		animationIterationCount: "infinite",
		"@media (prefers-reduced-motion: reduce)": {
			animation: "none",
		},
	},
	initBar: {
		height: "3px",
		marginTop: "4px",
		borderRadius: "2px",
		background: surfaceColors.line,
		overflow: "hidden",
	},
	initBarFill: {
		height: "100%",
		background: `linear-gradient(to right, ${brandColors.primary}, ${statusColors.cool})`,
		transitionProperty: "width",
		transitionDuration: "300ms",
		transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
		"@media (prefers-reduced-motion: reduce)": {
			transition: "none",
		},
	},
	emptyHint: {
		padding: "40px 16px",
		fontFamily: fonts.body,
		fontSize: "12px",
		color: inkColors.ink4,
		textAlign: "center",
	},
	row: {
		display: "flex",
		alignItems: "center",
		gap: "10px",
		padding: "6px 12px",
		position: "relative",
		cursor: "pointer",
		background: "none",
		border: "none",
		width: "100%",
		textAlign: "left",
		"@media (prefers-reduced-motion: reduce)": {
			transition: "none",
		},
	},
	rowRecede: {
		/* Recede effect removed — spine now remains static during drafting */
	},
	numberGutter: {
		// Wide enough for CMC Module 3 numbers ("3.2.S.1.2", ~54px in
		// 10px mono). CSR / CER / PSUR use shorter numbers ("9.1",
		// "13.2"); they sit right-aligned within the same width with
		// extra leading whitespace. marginLeft creates space for the
		// thread which now appears before the numbers (left:32px).
		// ``overflow: hidden`` is defensive: any future doctype with
		// even longer numbers truncates rather than bleeding into
		// the thread column.
		width: "56px",
		marginLeft: "36px",
		overflow: "hidden",
		fontSize: "10px",
		color: inkColors.ink4,
		textAlign: "right",
		fontVariantNumeric: "tabular-nums",
		fontFamily: fonts.mono,
		flexShrink: 0,
	},
	dotWrap: {
		position: "relative",
		width: "20px",
		height: "20px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		flexShrink: 0,
	},
	bloomRing: {
		position: "absolute",
		width: "60px",
		height: "60px",
		borderRadius: "50%",
		top: "calc(50% - 30px)",
		left: "calc(50% - 30px)",
		zIndex: 0,
		pointerEvents: "none",
		filter: "blur(8px)",
		background: `radial-gradient(circle, ${statusColors.hot}33 0%, transparent 70%)`,
		animationName: {
			"0%": { opacity: 0, transform: "scale(0.5)" },
			"100%": { opacity: 1, transform: "scale(1)" },
		},
		animationDuration: "400ms",
		animationTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)",
		"@media (prefers-reduced-motion: reduce)": {
			animation: "none",
		},
	},
	bloomWarn: {
		background: `radial-gradient(circle, ${statusColors.warn}33 0%, transparent 70%)`,
	},
	dot: {
		borderRadius: "50%",
		transitionProperty: "all",
		transitionDuration: `${motion.transitionMs}ms`,
		zIndex: 2,
		"@media (prefers-reduced-motion: reduce)": {
			transition: "none",
		},
	},
	dotCrystallize: {
		animationName: {
			"0%": { transform: "scale(0.7)", opacity: 0 },
			"100%": { transform: "scale(1)", opacity: 1 },
		},
		animationDuration: "350ms",
		animationTimingFunction: "cubic-bezier(0.68, -0.55, 0.265, 1.55)",
		"@media (prefers-reduced-motion: reduce)": {
			animation: "none",
		},
	},
	dotQueued: {
		width: "10px",
		height: "10px",
		background: "#fff",
		border: `1.5px solid ${surfaceColors.lineStrong}`,
	},
	// Conditional activation skipped this module (condition FALSE) — a
	// smaller, faded hollow dot with a dashed stroke so it reads as
	// "deliberately not drafted" rather than "still waiting".
	dotSkipped: {
		width: "10px",
		height: "10px",
		background: "#fff",
		border: `1.5px dashed ${surfaceColors.lineStrong}`,
		opacity: 0.55,
	},
	dotDrafting: {
		width: "14px",
		height: "14px",
		background: statusColors.hot,
		boxShadow: `0 0 0 6px ${statusColors.hot}22`,
	},
	dotDrafted: {
		width: "12px",
		height: "12px",
		background: statusColors.cool,
	},
	dotApproval: {
		width: "14px",
		height: "14px",
		background: statusColors.warn,
		boxShadow: `0 0 0 5px ${statusColors.warn}22`,
	},
	dotFlagged: {
		width: "12px",
		height: "12px",
		background: statusColors.danger,
		color: "#fff",
		fontSize: "9px",
		fontWeight: 800,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	pulse: {
		position: "absolute",
		inset: "-4px",
		borderRadius: "50%",
		animationName: {
			"0%": { boxShadow: "0 0 0 0px currentColor", opacity: 0.6 },
			"70%": { boxShadow: "0 0 0 8px currentColor", opacity: 0 },
			"100%": { boxShadow: "0 0 0 8px currentColor", opacity: 0 },
		},
		animationDuration: `${motion.pulseDurationMs}ms`,
		animationIterationCount: "infinite",
		animationTimingFunction: "ease-out",
		"@media (prefers-reduced-motion: reduce)": {
			animation: "none",
		},
	},
	pulseHot: { color: `${statusColors.hot}66` },
	pulseWarn: { color: `${statusColors.warn}66` },
	label: {
		flex: 1,
		minWidth: 0,
		fontFamily: fonts.body,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	labelQueued: {
		fontSize: "12px",
		fontWeight: 400,
		color: inkColors.ink4,
	},
	labelDone: {
		fontSize: "12px",
		fontWeight: 500,
		color: inkColors.ink,
	},
	labelActive: {
		fontSize: "13px",
		fontWeight: 700,
		color: inkColors.ink,
		letterSpacing: "-0.1px",
	},
	subline: {
		fontSize: "10px",
		fontWeight: 400,
		color: inkColors.ink3,
		marginTop: "2px",
		fontFamily: fonts.mono,
		display: "flex",
		alignItems: "center",
		gap: "6px",
	},
	sublineDot: {
		color: statusColors.hot,
	},
	sublineElapsed: {
		// Quieter than the subline text — the timer is supporting info,
		// not the headline. Same mono font for tabular numerics so the
		// counter doesn't jitter as digits change.
		color: inkColors.ink4,
		fontVariantNumeric: "tabular-nums",
	},
	approvalSubline: {
		fontSize: "10px",
		fontWeight: 500,
		color: statusColors.warn,
		marginTop: "2px",
		fontFamily: fonts.body,
		letterSpacing: "0.2px",
		textTransform: "uppercase",
	},
	// Spec 23 P5 — persistent subline on drafted sections with
	// accepted-time / word-count / citations / quality. Quieter than
	// the active-drafting subline (monochrome ink4; no hot-orange
	// leading dot) since it's a post-work summary rather than a live
	// status beat.
	sublineDrafted: {
		fontSize: "10px",
		fontWeight: 400,
		color: inkColors.ink4,
		marginTop: "2px",
		fontFamily: fonts.mono,
		marginLeft: "6px",
		fontVariantNumeric: "tabular-nums",
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	// Danger-toned subline for a redraft that couldn't be written into the live
	// doc (no matching heading — the doc likely needs a full Load).
	activityFeed: {
		display: "block",
		marginTop: "2px",
	},
	activityLine: {
		fontSize: "10px",
		fontWeight: 400,
		color: inkColors.ink4,
		fontFamily: fonts.mono,
		lineHeight: "1.35",
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	activityLineLive: {
		color: inkColors.ink3,
	},
	activityLineError: {
		color: statusColors.danger,
		fontWeight: 500,
	},
	activityElapsed: {
		color: inkColors.ink5,
		fontVariantNumeric: "tabular-nums",
	},
	sublineLoadFailed: {
		fontSize: "10px",
		fontWeight: 500,
		color: statusColors.danger,
		marginTop: "2px",
		marginLeft: "6px",
		fontFamily: fonts.body,
		letterSpacing: "0.2px",
	},
	rightMeta: {
		fontSize: "9px",
		color: inkColors.ink5,
		fontFamily: fonts.mono,
		flexShrink: 0,
	},
	rightMetaWarn: { color: statusColors.warn, fontWeight: 700 },
	rightMetaDanger: { color: statusColors.danger, fontWeight: 700 },
	// Spec 23 P6 — per-quality / per-findings rightMeta variants.
	// rightMetaLow is styled like rightMetaDanger (red, bold uppercase)
	// because a low-quality drafted section warrants writer attention;
	// rightMetaFindings uses warn (amber) because findings are advisory.
	rightMetaLow: {
		color: statusColors.danger,
		fontWeight: 700,
		letterSpacing: "0.5px",
	},
	rightMetaFindings: {
		color: statusColors.warn,
		fontWeight: 700,
		fontVariantNumeric: "tabular-nums",
	},
	rightMetaContentGap: {
		color: statusColors.danger,
		fontWeight: 700,
		fontVariantNumeric: "tabular-nums",
	},
	// Regulatory-audit flag count — blue to match the [REGULATOR FLAG: …]
	// inline marker styling and distinguish it from the red content-gap badge.
	rightMetaRegulator: {
		color: "#1565C0",
		fontWeight: 700,
		fontVariantNumeric: "tabular-nums",
	},
	floatingDock: {
		position: "absolute",
		left: "12px",
		right: "12px",
		bottom: "8px",
		zIndex: 6,
		pointerEvents: "none",
	},
	floatingRow: {
		pointerEvents: "auto",
		paddingLeft: "0",
		paddingRight: "0",
		borderRadius: "8px",
		background: "#FFFFFF",
		border: `1px solid ${surfaceColors.lineStrong}`,
		boxShadow: "0 6px 20px rgba(17,24,39,0.12)",
	},
	floatingNumberGutter: {
		marginLeft: "0",
		width: "auto",
		textAlign: "left",
	},
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Format the "accepted 2:14pm" time label from an epoch-ms timestamp.
 * Pure; testable. Uses the same lowercase-am/pm, no-leading-zero
 * grammar as TimelineView's formatEventTime so the cadence feels
 * consistent across surfaces.
 */
export function formatAcceptedAt(ts: number): string {
	const d = new Date(ts);
	const hour12 = d.toLocaleString("en-US", {
		hour: "numeric",
		minute: "2-digit",
		hour12: true,
	});
	return `accepted ${hour12.replace(/\s?(AM|PM)/i, (_, m) => m.toLowerCase())}`;
}

/** Pure — build the drafted-section subline from a SpineSection's
 *  post-work fields. Returns null when there is nothing to render
 *  (no acceptedAt, no wordCount, no citationCount, no quality —
 *  caller omits the subline entirely).
 *
 *  Format: "accepted Xpm · N words · K cites · quality Y".
 *  Missing fields drop out; the " · " separator logic preserves
 *  clean output regardless of which fields are present. */
export function formatDraftedSubline(section: {
	acceptedAt?: number;
	wordCount?: number;
	citationCount?: number;
	quality?: "high" | "medium" | "low";
	inputTokens?: number;
	outputTokens?: number;
	model?: string | null;
}): string | null {
	const parts: string[] = [];
	if (section.acceptedAt) {
		parts.push(formatAcceptedAt(section.acceptedAt));
	}
	if (section.wordCount !== undefined && section.wordCount > 0) {
		parts.push(`${section.wordCount} words`);
	}
	if (section.citationCount !== undefined && section.citationCount > 0) {
		parts.push(
			`${section.citationCount} cite${section.citationCount === 1 ? "" : "s"}`,
		);
	}
	if (section.quality) {
		parts.push(`quality ${section.quality}`);
	}
	// Per-section token usage + drafting model backend. Only shown once the
	// persisted telemetry is present (0/undefined on older runs).
	const inTok = section.inputTokens ?? 0;
	const outTok = section.outputTokens ?? 0;
	if (inTok > 0 || outTok > 0) {
		parts.push(`${formatTokens(inTok)} in · ${formatTokens(outTok)} out`);
	}
	if (section.model) {
		parts.push(section.model);
	}
	return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * Format a millisecond duration as the design's compact "Xs" / "Xm Ys"
 * shape. Sub-second precision below 10s ("2.1s") so the counter feels
 * live; whole-second above ("12s"); minutes after a minute ("1m 03s").
 * Pure function — testable in isolation.
 */
function formatElapsed(ms: number): string {
	if (ms < 0) return "0s";
	const totalSeconds = ms / 1000;
	if (totalSeconds < 10) {
		return `${totalSeconds.toFixed(1)}s`;
	}
	if (totalSeconds < 60) {
		return `${Math.floor(totalSeconds)}s`;
	}
	const minutes = Math.floor(totalSeconds / 60);
	const remSeconds = Math.floor(totalSeconds % 60);
	return `${minutes}m ${String(remSeconds).padStart(2, "0")}s`;
}

function dotClassFor(
	status: SpineSection["status"],
	styles: ReturnType<typeof useStyles>,
): string {
	switch (status) {
		case "drafting":
			return mergeClasses(styles.dot, styles.dotDrafting);
		case "drafted":
			return mergeClasses(styles.dot, styles.dotDrafted);
		case "needs_approval":
			return mergeClasses(styles.dot, styles.dotApproval);
		case "flagged":
			return mergeClasses(styles.dot, styles.dotFlagged);
		case "skipped":
			return mergeClasses(styles.dot, styles.dotSkipped);
		default:
			return mergeClasses(styles.dot, styles.dotQueued);
	}
}

function shouldPulse(status: SpineSection["status"]): boolean {
	return status === "drafting" || status === "needs_approval";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const LoomSpine: React.FC<LoomSpineProps> = ({
	sections,
	activeSectionId,
	onSectionFocus,
	onOpenReview,
	streamError,
	pendingApproval,
	onApprovalAccept,
	onApprovalOpenReview,
	countersBySection,
	initStatus,
	pauseAnimations = false,
	liveDocLoadBySection,
	agentActivityBySection,
}) => {
	const styles = useStyles();
	const activeIdx = sections.findIndex((s) => s.id === activeSectionId);
	const activeSection =
		activeSectionId === null
			? null
			: sections.find((s) => s.id === activeSectionId) ?? null;

	// Neural Thread fill — grows as sections complete. On post-run
	// (no active section, none queued or drafting) fill to 100% for
	// the completion sweep.
	const isComplete =
		activeSectionId === null &&
		sections.length > 0 &&
		sections.every((s) => s.status !== "queued" && s.status !== "drafting");
	let fillPercent = 0;
	if (sections.length > 0) {
		if (isComplete) {
			fillPercent = 100;
		} else if (activeIdx >= 0) {
			fillPercent = (activeIdx / sections.length) * 100;
		}
	}

	// Approval state — drives mote amber bleed + bloom ring color shift.
	const isApprovalState = !!pendingApproval;

	// Live elapsed-time tick for the active row's subline. One shared
	// tick state at the root drives all rows' elapsed displays — N
	// active sections (rare) re-render together rather than each
	// wiring its own interval. Pauses when no section is active.
	// Cadence is 1000ms — the elapsed display is minute-resolution, so
	// anything faster just thrashes the spine's render tree (and
	// re-runs descendant effects) without the writer seeing any
	// benefit. The earlier 250ms cadence was a pre-2026-05 default
	// that contributed to the "Word feels laggy when the pane is open"
	// symptom alongside the Office.js round-trip pile-up.
	const [now, setNow] = useState(() => Date.now());
	// Also tick while a section is being written into Word — those trackers can
	// run post-run (no active section) and still show a live elapsed reading.
	const loadingIntoWord = !!liveDocLoadBySection && hasActiveLiveDocLoad(liveDocLoadBySection);
	const anyActivity = hasAnyActivity(agentActivityBySection);
	useEffect(() => {
		if ((!activeSectionId && !loadingIntoWord && !anyActivity) || pauseAnimations) return;
		const t = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(t);
	}, [activeSectionId, loadingIntoWord, anyActivity, pauseAnimations]);

	// Auto-scroll the active row into view as drafting advances. The
	// design fixture fits every section in one pane so never had to
	// address this; real runs (38-section CSR) overflow, and without
	// a scroll cue the halo pulse disappears from view the moment
	// drafting moves past the visible range. ``block: "nearest"`` only
	// scrolls when the row is actually off-screen — no jitter when it
	// was already in view.
	//
	// # DECISION (2026-05-06): suppress auto-scroll for 3s after the
	// # writer manually scrolls. Pre-2026-05-06 the effect re-fired on
	// # every activeSectionId change, which yanked the writer's view
	// # back whenever they tried to look at an earlier section — the
	// # auto-scroll fought the writer's intent. The grace window means:
	// # if the writer scrolls, we leave them alone for 3s; if drafting
	// # advances during that window, the halo pulse may go off-screen
	// # but the writer keeps control. After 3s of no manual scroll,
	// # auto-follow resumes.
	// # WHY: writer agency > follow-the-agent affordance. A surprising
	// # auto-scroll is the worst kind of ambient UI — it makes the
	// # writer feel like the app is fighting them.
	const activeRowRef = useRef<HTMLButtonElement | null>(null);
	const threadDoneRef = useRef<HTMLDivElement | null>(null);
	const viewportRef = useRef<HTMLDivElement | null>(null);
	const lastUserScrollRef = useRef<number>(0);
	const programmaticScrollUntilRef = useRef<number>(0);
	const [isActiveRowVisible, setIsActiveRowVisible] = useState(true);

	const recomputeActiveRowVisibility = useCallback(() => {
		if (!activeSectionId) {
			setIsActiveRowVisible(true);
			return;
		}
		const viewportEl = viewportRef.current;
		const activeRowEl = activeRowRef.current;
		if (!viewportEl || !activeRowEl) {
			setIsActiveRowVisible(false);
			return;
		}
		const viewportRect = viewportEl.getBoundingClientRect();
		const rowRect = activeRowEl.getBoundingClientRect();
		const intersects =
			rowRect.bottom > viewportRect.top && rowRect.top < viewportRect.bottom;
		setIsActiveRowVisible(intersects);
	}, [activeSectionId]);

	// Track manual scrolls. We can't perfectly distinguish a user wheel
	// event from a programmatic scrollIntoView, but we can record when
	// we KNOW we just initiated a programmatic scroll — any onScroll
	// event within ~600ms of that is probably the smooth-scroll
	// animation, not the user. Beyond that window, treat onScroll as
	// user intent.
	const handleScroll = useCallback(() => {
		if (Date.now() < programmaticScrollUntilRef.current) return;
		lastUserScrollRef.current = Date.now();
		recomputeActiveRowVisibility();
	}, [recomputeActiveRowVisibility]);

	useEffect(() => {
		recomputeActiveRowVisibility();
	}, [recomputeActiveRowVisibility, activeSectionId, sections]);

	useEffect(() => {
		const handleResize = () => recomputeActiveRowVisibility();
		globalThis.addEventListener("resize", handleResize);
		return () => globalThis.removeEventListener("resize", handleResize);
	}, [recomputeActiveRowVisibility]);

	useEffect(() => {
		if (!activeSectionId || !activeRowRef.current) return;
		const ts = Date.now();
		// Skip if the writer scrolled within the last 3s — they're driving.
		if (ts - lastUserScrollRef.current < 3000) return;
		programmaticScrollUntilRef.current = ts + 600;
		activeRowRef.current.scrollIntoView({
			block: "nearest",
			behavior: "smooth",
		});
		const t = globalThis.setTimeout(() => recomputeActiveRowVisibility(), 650);
		return () => globalThis.clearTimeout(t);
	}, [activeSectionId, recomputeActiveRowVisibility]);

	const handleFloatingActiveClick = useCallback(() => {
		if (!activeSection) return;
		onSectionFocus?.(activeSection.id);
		const row = activeRowRef.current;
		if (!row) return;
		const ts = Date.now();
		programmaticScrollUntilRef.current = ts + 600;
		row.scrollIntoView({ block: "nearest", behavior: "smooth" });
		globalThis.setTimeout(() => recomputeActiveRowVisibility(), 650);
	}, [activeSection, onSectionFocus, recomputeActiveRowVisibility]);

	const showFloatingActiveRow =
		activeSection !== null &&
		activeSection.status === "drafting" &&
		!isActiveRowVisible;

	useEffect(() => {
		if (!threadDoneRef.current) return;
		threadDoneRef.current.style.height = `${fillPercent}%`;
	}, [fillPercent]);

	// Init checklist — derived fresh from the latest init event (pure,
	// no accumulation). Null checklist = unknown stage (older server) →
	// message-only fallback banner. The message-only path still shows the
	// progress bar when the event carries counts (activation resolution).
	const initChecklist = initStatus ? deriveInitChecklist(initStatus) : null;
	const initActiveProgress =
		initChecklist?.find((step) => step.state === "active")?.progress ??
		(initStatus &&
		typeof initStatus.done === "number" &&
		typeof initStatus.total === "number" &&
		initStatus.total > 0
			? { done: initStatus.done, total: initStatus.total }
			: null);

	return (
		<div className={styles.root}>
			<div
				ref={viewportRef}
				className={styles.viewport}
				onScroll={handleScroll}
				data-spine-viewport="true"
			>
				{streamError && <div className={styles.errorBanner}>{streamError}</div>}
				{!streamError && initStatus && (
					<div className={styles.initPanel}>
						<div className={styles.initPanelTitle}>
							<span className={styles.initBannerIcon}>⧗</span>
							Preparing run
						</div>
						{initChecklist === null ? (
							<div className={styles.initStepLabel}>{initStatus.message}</div>
						) : (
							initChecklist.map((step) => (
								<div key={step.id} className={styles.initStep}>
									<span
										aria-hidden="true"
										className={mergeClasses(
											styles.initStepMarker,
											step.state === "done" && styles.initStepMarkerDone,
											step.state === "active" && styles.initStepMarkerActive,
										)}
									/>
									<span
										className={
											step.state === "pending"
												? styles.initStepPending
												: styles.initStepLabel
										}
									>
										{step.label}
									</span>
									{step.progress && (
										<span className={styles.initStepCount}>
											{step.progress.done}/{step.progress.total}
										</span>
									)}
								</div>
							))
						)}
						{initActiveProgress && (
							<div className={styles.initBar}>
								<div
									className={styles.initBarFill}
									style={{
										width: `${(initActiveProgress.done / Math.max(1, initActiveProgress.total)) * 100}%`,
									}}
								/>
							</div>
						)}
					</div>
				)}

			

				{sections.length === 0 && (
					<div className={styles.emptyHint}>
						Waiting for the first section to start…
					</div>
				)}

				{sections.map((section, i) => {
					const isActive = section.id === activeSectionId;
					const showPopoutHere =
						pendingApproval?.sectionId === section.id &&
						onApprovalAccept &&
						onApprovalOpenReview;
					// Visual safety: only the current active row should render
					// as drafting/orange. If upstream state briefly leaves a
					// non-active row in "drafting", clamp its marker to queued
					// so the spine never shows multiple orange active markers.
					const visualStatus: SpineSection["status"] =
						!isActive && section.status === "drafting"
							? "queued"
							: section.status;
					// Depth-of-field: nodes more than 2 away from the active one
					// recede to 35% during a live run. No recede at all when
					// there's no active node (idle of mid-run, post-run review).
					const distance = activeIdx >= 0 ? Math.abs(i - activeIdx) : 0;
					const recede = activeIdx >= 0 && distance > 2;

					let labelCls = styles.labelDone;
					if (isActive) {
						labelCls = styles.labelActive;
					} else if (section.status === "queued") {
						labelCls = styles.labelQueued;
					}

					const showPulse = !pauseAnimations && shouldPulse(visualStatus);
					const pulseColorCls =
						visualStatus === "drafting" ? styles.pulseHot : styles.pulseWarn;
					// Hierarchy: indent the TITLE only, by depth of the display
					// number ("9.3.1" → 2 steps). Gutter + dot stay put so the
					// vertical thread remains straight; the taskpane is narrow,
					// hence the small step.
					const titleIndent = (spineDepth(section.number) - 1) * SPINE_INDENT_PX;

					return (
						<React.Fragment key={section.id}>
							<button
								type="button"
								ref={isActive ? activeRowRef : undefined}
								className={mergeClasses(styles.row, recede && styles.rowRecede)}
								onClick={() => onSectionFocus?.(section.id)}
								aria-current={isActive ? "step" : undefined}
								aria-label={`${formatGutterNumber(section.number) ? `Section ${section.number}: ` : ""}${section.title} (${section.status})`}
								data-active-row={isActive ? "true" : undefined}
							>
								<span className={styles.numberGutter}>
									{formatGutterNumber(section.number)}
								</span>

								<span className={styles.dotWrap}>
									{/* Bloom ring — ambient glow behind the active node.
                  Shifts orange → amber when approval is pending. */}
									{isActive && (
										<span
											aria-hidden="true"
											className={mergeClasses(
												styles.bloomRing,
												isApprovalState && styles.bloomWarn,
											)}
										/>
									)}
									{/* Dot — key changes on status so React remounts it,
                  triggering the crystallize bounce on drafted. */}
									<span
										key={visualStatus}
										className={mergeClasses(
											dotClassFor(visualStatus, styles),
											section.status === "drafted" && styles.dotCrystallize,
										)}
									>
										{/* Spec 23 P6 — '!' glyph now appears on both flagged
                    AND needs_approval rows. Different colors (danger
                    vs warn) come from dotClassFor; the glyph is the
                    same universal "attention" signal. */}
										{(section.status === "flagged" ||
											section.status === "needs_approval") &&
											"!"}
									</span>
									{showPulse && (
										<span
											aria-hidden="true"
											className={mergeClasses(styles.pulse, pulseColorCls)}
										/>
									)}
								</span>

								<span
									className={mergeClasses(styles.label, labelCls)}
									style={titleIndent > 0 ? { paddingLeft: `${titleIndent}px` } : undefined}
								>
									{section.title}
									{isActive &&
										section.status === "drafting" &&
										section.subline && (
											<span className={styles.subline}>
												<span className={styles.sublineDot}>●</span>
												{section.subline}
												{section.sublineStartedAt && (
													<span className={styles.sublineElapsed}>
														&nbsp;·&nbsp;
														{formatElapsed(now - section.sublineStartedAt)}
													</span>
												)}
											</span>
										)}
									{isActive && section.status === "needs_approval" && (
										<span className={styles.approvalSubline}>
											needs your call
										</span>
									)}
									{/* Spec 23 P5 — persistent rich subline on drafted rows.
                  Carries acceptedAt / wordCount / citationCount /
                  quality; the formatter drops missing fields so
                  partial data still renders cleanly. Hidden when
                  the formatter returns null (no post-work fields
                  populated yet). */}
									{section.status === "drafted" &&
										(() => {
											const sub = formatDraftedSubline(section);
											return sub ? (
												<span className={styles.sublineDrafted}>{sub}</span>
											) : null;
										})()}
									{/* Custom submission: which template section this heading was
									    matched to (persisted on the spine so it survives reload). */}
									{section.matchedTitle && (
										<span className={styles.sublineDrafted}>
											↳ Template: {section.matchedTitle}
										</span>
									)}
									{/* Per-section "loading into Word…" tracker (post-redraft
									    live-doc write). Renders regardless of status and is
									    driven by shell state, so it survives the SSE subline
									    wipe. ``loading`` shows a pulse dot + elapsed; ``failed``
									    shows a danger subline pointing at Load. */}
									{(() => {
										const ld = liveDocLoadBySection?.get(section.id);
										if (!ld) return null;
										if (ld.state === "loading") {
											return (
												<span className={styles.subline}>
													<span className={styles.sublineDot}>●</span>
													loading into Word…
													<span className={styles.sublineElapsed}>
														&nbsp;·&nbsp;
														{formatElapsed(now - ld.startedAt)}
													</span>
												</span>
											);
										}
										return (
											<span className={styles.sublineLoadFailed}>couldn't load into Word — use Load</span>
										);
									})()}
								</span>

								{section.status === "flagged" && (
									<span
										className={mergeClasses(
											styles.rightMeta,
											styles.rightMetaDanger,
										)}
									>
										FLAG
									</span>
								)}
								{/* Spec 23 P6 — LOW red badge when a drafted section's
                quality verdict is 'low'. Different dimension from
                the 'flagged' status above: a drafted section can
                still render cleanly but carry a LOW quality tag. */}
								{section.status === "drafted" && section.quality === "low" && (
									<span
										className={mergeClasses(
											styles.rightMeta,
											styles.rightMetaLow,
										)}
									>
										LOW
									</span>
								)}
								{section.status === "drafted" &&
									section.contentGapCount !== undefined &&
									section.contentGapCount > 0 && (
										<span
											className={mergeClasses(
												styles.rightMeta,
												styles.rightMetaContentGap,
											)}
										>
											{section.contentGapCount}⚑
										</span>
									)}
								{/* Regulatory-audit flag count 'N⚖' — persisted by the
                audit and surfaced on reopen via the spine snapshot, so a
                reviewed section shows its outstanding regulator flags
                without re-running the audit. Clicking it opens the section's
                Review tab, where the persisted findings (mini-reports) render. */}
								{section.regulatorFlagCount !== undefined &&
									section.regulatorFlagCount > 0 && (
										<span
											className={mergeClasses(
												styles.rightMeta,
												styles.rightMetaRegulator,
											)}
											title="View regulatory review findings"
											role={onOpenReview ? "button" : undefined}
											tabIndex={onOpenReview ? 0 : undefined}
											aria-label={
												onOpenReview
													? `View ${section.regulatorFlagCount} regulatory findings`
													: undefined
											}
											style={onOpenReview ? { cursor: "pointer" } : undefined}
											onClick={
												onOpenReview
													? (e) => {
															e.stopPropagation();
															onOpenReview(section.id);
														}
													: undefined
											}
											onKeyDown={
												onOpenReview
													? (e) => {
															if (e.key === "Enter" || e.key === " ") {
																e.preventDefault();
																e.stopPropagation();
																onOpenReview(section.id);
															}
														}
													: undefined
											}
										>
											{section.regulatorFlagCount}⚖
										</span>
									)}
								{/* Spec 23 P6 — findings-count flag 'N⚐' on drafted rows
                with unresolved issues from the review-agent sidecar.
                Shown alongside (not instead of) LOW when both apply. */}
								{section.status === "drafted" &&
									section.flaggedCount !== undefined &&
									section.flaggedCount > 0 && (
										<span
											className={mergeClasses(
												styles.rightMeta,
												styles.rightMetaFindings,
											)}
										>
											{section.flaggedCount}⚐
										</span>
									)}
								{/* Cumulative event pill — per-kind tags + auto-star when
                quality is "high" AND the section has been regenerated
                (Spec 22 §4.5 Option A, accepted 2026-04-22). Star
                semantics: "writer saw this section through at least
                one redraft to a passing verdict" — a milestone. */}
								{countersBySection &&
									(() => {
										const counters = countersBySection.get(section.id);
										if (!counters) return null;
										const showStar =
											section.status === "drafted" &&
											section.quality === "high" &&
											counters.regens > 0;
										return (
											<SpineTagStrip counters={counters} showStar={showStar} />
										);
									})()}
							</button>
							{showPopoutHere && pendingApproval && (
								<ApprovalPopout
									approval={pendingApproval}
									onAccept={onApprovalAccept}
									onOpenReview={onApprovalOpenReview}
								/>
							)}
						</React.Fragment>
					);
				})}
			</div>

			{showFloatingActiveRow && activeSection && (
				<div className={styles.floatingDock}>
					<button
						type="button"
						className={mergeClasses(styles.row, styles.floatingRow)}
						onClick={handleFloatingActiveClick}
						aria-label={`Jump to active section ${formatGutterNumber(activeSection.number) ? `${activeSection.number}: ` : ""}${activeSection.title}`}
						data-testid="floating-active-section-row"
					>
						<span className={mergeClasses(styles.numberGutter, styles.floatingNumberGutter)}>
							{formatGutterNumber(activeSection.number)}
						</span>
						<span className={styles.dotWrap}>
							<span aria-hidden="true" className={styles.bloomRing} />
							<span className={dotClassFor(activeSection.status, styles)} />
							{!pauseAnimations && (
								<span
									aria-hidden="true"
									className={mergeClasses(styles.pulse, styles.pulseHot)}
								/>
							)}
						</span>
						<span className={mergeClasses(styles.label, styles.labelActive)}>
							{activeSection.title}
							{(() => {
								const feed = agentActivityBySection?.get(activeSection.id);
								const latest = feed && feed.length > 0 ? feed[feed.length - 1] : null;
								if (latest) {
									return (
										<span
											className={mergeClasses(
												styles.subline,
												latest.kind === "error" && styles.activityLineError,
											)}
										>
											<span className={styles.sublineDot}>●</span>
											{latest.text}
											<span className={styles.sublineElapsed}>
												&nbsp;·&nbsp;
												{formatElapsed(now - latest.at)}
											</span>
										</span>
									);
								}
								return activeSection.subline ? (
									<span className={styles.subline}>
										<span className={styles.sublineDot}>●</span>
										{activeSection.subline}
										{activeSection.sublineStartedAt && (
											<span className={styles.sublineElapsed}>
												&nbsp;·&nbsp;
												{formatElapsed(now - activeSection.sublineStartedAt)}
											</span>
										)}
									</span>
								) : null;
							})()}
						</span>
					</button>
				</div>
			)}
		</div>
	);
};

export default LoomSpine;
